/**
 * Contexto operacional isolado da Quotation Calculator (cliente roteado).
 *
 * Guarda em memória (nunca em storage) o token/tenant de operação emitidos
 * pelo endpoint /auth/cross-tenant-exchange. NÃO substitui o TokenService
 * global: é lido apenas pelos serviços da calculadora via overrides.
 *
 * Regras:
 * - só existe enquanto um cliente roteado está selecionado;
 * - é limpo ao cancelar, trocar cliente ou fechar a ferramenta.
 */

export interface QuotationOperationContext {
	sourceTenantId: string | null;
	effectiveTenantId: string;
	operationAccessToken: string;
	routing: {
		routingConfigId: number;
		targetClientId: number;
		targetUserId: number;
		targetUserLogin: string;
	};
	expiresAt: string;
}

let current: QuotationOperationContext | null = null;
const listeners = new Set<(ctx: QuotationOperationContext | null) => void>();

function emit(): void {
	for (const listener of listeners) {
		listener(current);
	}
}

export const QuotationOperationContextStore = {
	/** Define o contexto operacional (cliente roteado selecionado). */
	set(ctx: QuotationOperationContext): void {
		current = ctx;
		emit();
	},

	/** Limpa o contexto (volta ao fluxo normal / tenant global). */
	clear(): void {
		if (current === null) {
			return;
		}
		current = null;
		emit();
	},

	/** Retorna o contexto atual ou null quando em modo normal. */
	get(): QuotationOperationContext | null {
		return current;
	},

	/** Indica se há um contexto de operação roteado ativo. */
	isActive(): boolean {
		return current !== null;
	},

	/** Retorna o override de token/tenant para os serviços, se ativo. */
	getOverride(): { accessToken: string; tenantId: string } | null {
		if (current === null) {
			return null;
		}
		return {
			accessToken: current.operationAccessToken,
			tenantId: current.effectiveTenantId,
		};
	},

	/** Inscreve um listener para mudanças de contexto. Retorna unsubscribe. */
	subscribe(
		listener: (ctx: QuotationOperationContext | null) => void,
	): () => void {
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	},
};
